import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { CalendarDays, Copy, AlertTriangle, Trash2, ChevronLeft, CheckCircle2 } from 'lucide-react';

const STATUS_OPTIONS = ['confirmed', 'tentative', 'cancelled', 'completed', 'no_show'];
const STATUS_LABEL_KEYS = {
  confirmed: 'bookingCal.st_confirmed', tentative: 'bookingCal.st_tentative',
  cancelled: 'bookingCal.st_cancelled', completed: 'bookingCal.st_completed', no_show: 'bookingCal.st_no_show',
};
const ymd = (d) => format(d, 'yyyy-MM-dd');
const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));
// Two HH:MM ranges on the same day overlap when each starts before the other ends.
const overlaps = (aS, aE, bS, bE) => aS < bE && bS < aE;

const emptyItem = (dateStr, assigneeUserId) => ({
  _key: uid(), date: dateStr, startTime: '09:00', endTime: '10:00',
  clientName: '', service: '', notes: '', phone: '', email: '', status: 'confirmed',
  assigneeUserId: assigneeUserId || '',
  payStatus: 'unpaid', payPrice: '', payAmount: '', payMethod: 'not_specified',
});

export default function MultiDateBooking({ open, onOpenChange, calendar, members, existingBookings, currentUserId, onCreated }) {
  const { t } = useTranslation();
  const [step, setStep] = useState('dates');       // dates | edit | review
  const [selected, setSelected] = useState([]);     // Date[]
  const [items, setItems] = useState([]);            // per-appointment rows
  const [saving, setSaving] = useState(false);
  const [batchId, setBatchId] = useState(uid);       // fresh id per save session
  const savingRef = useRef(false);                   // race-safe in-flight guard
  // Every time the dialog opens, start a brand-new batch id so a second set of
  // appointments in the same page session is never mistaken for a duplicate.
  useEffect(() => { if (open) setBatchId(uid()); }, [open]);

  // Dedupe members by userId (QA seeds can contain duplicates) and keep a lookup.
  const memberList = useMemo(() => {
    const seen = new Set(); const out = [];
    (members || []).forEach(m => { if (m.userId && !seen.has(m.userId)) { seen.add(m.userId); out.push(m); } });
    return out;
  }, [members]);
  const memberName = (idv) => (memberList.find(m => m.userId === idv) || {}).userName
    || (memberList.find(m => m.userId === idv) || {}).userEmail || '';

  const reset = () => { setStep('dates'); setSelected([]); setItems([]); setSaving(false); savingRef.current = false; };
  const close = () => { onOpenChange(false); setTimeout(reset, 200); };

  const goEdit = () => {
    if (!selected.length) { toast.error(t('bookingCal.md_pickAtLeastOne', 'Please select at least one date')); return; }
    const dates = [...selected].sort((a, b) => a - b).map(ymd);
    // Keep any edits already made for dates that are still selected.
    setItems(prev => {
      const byDate = {}; prev.forEach(it => { byDate[it.date] = it; });
      return dates.map(d => byDate[d] || emptyItem(d, currentUserId));
    });
    setStep('edit');
  };

  const setItem = (key, patch) => setItems(prev => prev.map(it => it._key === key ? { ...it, ...patch } : it));
  const removeItem = (key) => setItems(prev => prev.filter(it => it._key !== key));

  // Copy everything EXCEPT the date from the first appointment to all others.
  const copyToAll = () => {
    if (!items.length) return;
    const src = items[0];
    const { _key, date, ...rest } = src;
    setItems(prev => prev.map((it, i) => i === 0 ? it : { ...it, ...rest }));
    toast.success(t('bookingCal.md_copied', 'Details copied to all appointments'));
  };

  // Conflict map: for each item, which other items / existing bookings clash.
  const conflicts = useMemo(() => {
    const active = (existingBookings || []).filter(b => !b.deletedAt);
    const map = {};
    items.forEach((it, idx) => {
      const samePerson = []; let generalOverlap = false;
      // vs other items in this batch
      items.forEach((o, j) => {
        if (j === idx) return;
        if (o.date === it.date && overlaps(it.startTime, it.endTime, o.startTime, o.endTime)
          && it.assigneeUserId && o.assigneeUserId && it.assigneeUserId === o.assigneeUserId) {
          samePerson.push(t('bookingCal.md_inThisList', 'another appointment in this list'));
        }
      });
      // vs existing bookings
      active.forEach(b => {
        if (b.date !== it.date || b.status === 'cancelled') return;
        if (!overlaps(it.startTime, it.endTime, b.startTime, b.endTime)) return;
        if (b.assigneeUserId) {
          if (b.assigneeUserId === it.assigneeUserId) samePerson.push(`${b.clientName} (${b.startTime})`);
        } else {
          generalOverlap = true;
        }
      });
      if (samePerson.length || generalOverlap) map[it._key] = { samePerson, generalOverlap };
    });
    return map;
  }, [items, existingBookings, t]);

  const hasConflicts = Object.keys(conflicts).length > 0;

  const validate = () => {
    for (const it of items) {
      if (!it.clientName.trim()) return t('bookingCal.md_clientRequired', 'Every appointment needs a client name');
      if (!it.assigneeUserId) return t('bookingCal.md_assigneeRequired', 'Every appointment needs an assigned person');
      if (it.endTime <= it.startTime) return t('bookingCal.md_endAfterStart', 'End time must be after start time');
    }
    return null;
  };

  const goReview = () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setStep('review');
  };

  const saveAll = async () => {
    if (savingRef.current) return;            // race-safe guard against double-tap
    savingRef.current = true;
    setSaving(true);
    try {
      const payload = {
        batchId,
        items: items.map(it => ({
          date: it.date, startTime: it.startTime, endTime: it.endTime,
          clientName: it.clientName.trim(), service: it.service.trim(), notes: it.notes,
          phone: it.phone.trim(), email: it.email.trim(), status: it.status,
          assigneeUserId: it.assigneeUserId,
          payment: it.payStatus === 'unpaid' && !it.payPrice ? null : {
            status: it.payStatus,
            priceCents: Math.round(parseFloat(it.payPrice || '0') * 100) || 0,
            amountPaidCents: Math.round(parseFloat(it.payAmount || '0') * 100) || 0,
            paymentMethod: it.payMethod,
          },
        })),
      };
      const r = await axiosInstance.post(`/calendar/calendars/${calendar.id}/bookings/bulk-individual`, payload);
      if (r.data?.alreadySaved) {
        toast.success(t('bookingCal.md_alreadySaved', 'These appointments were already saved'));
      } else {
        toast.success(t('bookingCal.md_saved', '{n} appointments created').replace('{n}', r.data?.created ?? items.length));
      }
      close();
      onCreated && onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('bookingCal.md_saveFailed', 'Could not save appointments'));
      savingRef.current = false;
      setSaving(false);
    }
  };

  const money = (cents) => `€${((cents || 0) / 100).toFixed(2)}`;

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) close(); }}>
      <DialogContent className="max-w-2xl w-[96vw] max-h-[92dvh] overflow-y-auto" data-testid="multidate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-purple-600" />
            {t('bookingCal.md_title', 'Select dates & edit individually')}
          </DialogTitle>
        </DialogHeader>

        {/* STEP 1 — pick dates */}
        {step === 'dates' && (
          <div data-testid="multidate-step-dates">
            <p className="text-[13px] text-gray-600 mb-2">
              {t('bookingCal.md_pickHint', 'Tap dates to select them. Tap again to remove. You can move across months and years.')}
            </p>
            <div className="flex justify-center">
              <Calendar
                mode="multiple"
                selected={selected}
                onSelect={(v) => setSelected(v || [])}
                weekStartsOn={1}
                captionLayout="dropdown-buttons"
                fromYear={new Date().getFullYear() - 1}
                toYear={new Date().getFullYear() + 3}
                classNames={{
                  caption_label: 'hidden',
                  caption_dropdowns: 'flex gap-1 justify-center',
                  vhidden: 'sr-only',
                  dropdown: 'border rounded-md px-1 py-0.5 text-sm bg-white',
                  dropdown_month: 'relative', dropdown_year: 'relative',
                }}
                data-testid="multidate-calendar"
              />
            </div>
            <div className="mt-3 flex items-center justify-between gap-2">
              <span className="text-[13px] font-semibold" data-testid="multidate-count">
                {t('bookingCal.md_selectedCount', '{n} dates selected').replace('{n}', selected.length)}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={close} data-testid="multidate-cancel">{t('cancel', 'Cancel')}</Button>
                <Button onClick={goEdit} disabled={!selected.length} data-testid="multidate-continue">
                  {t('bookingCal.md_continue', 'Continue')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 — edit each appointment */}
        {step === 'edit' && (
          <div data-testid="multidate-step-edit">
            <div className="flex items-center justify-between gap-2 mb-2">
              <Button variant="ghost" size="sm" onClick={() => setStep('dates')} data-testid="multidate-back-dates">
                <ChevronLeft className="h-4 w-4 mr-1" />{t('bookingCal.md_changeDates', 'Change dates')}
              </Button>
              <Button variant="secondary" size="sm" onClick={copyToAll} data-testid="multidate-copy-all">
                <Copy className="h-4 w-4 mr-1" />{t('bookingCal.md_copyAll', 'Copy details to all')}
              </Button>
            </div>
            {hasConflicts && (
              <div className="mb-2 rounded-lg bg-amber-50 border border-amber-300 p-2 text-[12px] text-amber-800 flex items-start gap-2" data-testid="multidate-conflict-banner">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                {t('bookingCal.md_conflictBanner', 'Some appointments overlap. Review the highlighted ones — you can change them or save anyway.')}
              </div>
            )}
            <div className="space-y-3">
              {items.map((it, i) => {
                const c = conflicts[it._key];
                return (
                  <div key={it._key} className={`rounded-xl border p-3 ${c ? 'border-amber-400 bg-amber-50/40' : 'border-gray-200'}`} data-testid={`multidate-item-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[13px] font-bold text-gray-800">{t('bookingCal.md_appt', 'Appointment')} {i + 1}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => removeItem(it._key)} data-testid={`multidate-remove-${i}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {c && (
                      <p className="text-[11px] text-amber-700 mb-2 flex items-center gap-1" data-testid={`multidate-conflict-${i}`}>
                        <AlertTriangle className="h-3 w-3" />
                        {c.samePerson.length
                          ? t('bookingCal.md_samePerson', 'Same person already booked: {x}').replace('{x}', c.samePerson.join(', '))
                          : t('bookingCal.md_generalOverlap', 'Overlaps an existing booking on this calendar')}
                      </p>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <div><Label className="text-[11px]">{t('bookingCal.date', 'Date')}</Label>
                        <Input type="date" value={it.date} onChange={e => setItem(it._key, { date: e.target.value })} data-testid={`multidate-date-${i}`} /></div>
                      <div><Label className="text-[11px]">{t('bookingCal.start', 'Start')}</Label>
                        <Input type="time" value={it.startTime} onChange={e => setItem(it._key, { startTime: e.target.value })} data-testid={`multidate-start-${i}`} /></div>
                      <div><Label className="text-[11px]">{t('bookingCal.end', 'End')}</Label>
                        <Input type="time" value={it.endTime} onChange={e => setItem(it._key, { endTime: e.target.value })} data-testid={`multidate-end-${i}`} /></div>
                      <div className="col-span-2 sm:col-span-1"><Label className="text-[11px]">{t('bookingCal.md_assignee', 'Assigned to')}</Label>
                        <Select value={it.assigneeUserId || ''} onValueChange={v => setItem(it._key, { assigneeUserId: v })}>
                          <SelectTrigger className="h-9" data-testid={`multidate-assignee-${i}`}><SelectValue placeholder={t('bookingCal.md_choosePerson', 'Choose person')} /></SelectTrigger>
                          <SelectContent>
                            {memberList.map(m => <SelectItem key={m.userId} value={m.userId} data-testid={`multidate-assignee-${i}-${m.userId}`}>{m.userName || m.userEmail}</SelectItem>)}
                          </SelectContent>
                        </Select></div>
                      <div><Label className="text-[11px]">{t('bookingCal.status', 'Status')}</Label>
                        <Select value={it.status} onValueChange={v => setItem(it._key, { status: v })}>
                          <SelectTrigger className="h-9" data-testid={`multidate-status-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{t(STATUS_LABEL_KEYS[s], s)}</SelectItem>)}</SelectContent>
                        </Select></div>
                      <div className="col-span-2 sm:col-span-3"><Label className="text-[11px]">{t('bookingCal.client', 'Client name')}</Label>
                        <Input value={it.clientName} onChange={e => setItem(it._key, { clientName: e.target.value })} maxLength={160} data-testid={`multidate-client-${i}`} /></div>
                      <div className="col-span-2 sm:col-span-3"><Label className="text-[11px]">{t('bookingCal.service', 'Title / service')}</Label>
                        <Input value={it.service} onChange={e => setItem(it._key, { service: e.target.value })} maxLength={200} data-testid={`multidate-service-${i}`} /></div>
                      <div className="col-span-2 sm:col-span-3"><Label className="text-[11px]">{t('bookingCal.notes', 'Description / notes')}</Label>
                        <Textarea rows={2} value={it.notes} onChange={e => setItem(it._key, { notes: e.target.value })} maxLength={10000} data-testid={`multidate-notes-${i}`} /></div>
                      <div><Label className="text-[11px]">{t('bookingCal.phone', 'Telephone')}</Label>
                        <Input value={it.phone} onChange={e => setItem(it._key, { phone: e.target.value })} maxLength={60} data-testid={`multidate-phone-${i}`} /></div>
                      <div className="col-span-2"><Label className="text-[11px]">{t('bookingCal.email', 'Email')}</Label>
                        <Input value={it.email} onChange={e => setItem(it._key, { email: e.target.value })} maxLength={200} data-testid={`multidate-email-${i}`} /></div>
                    </div>
                    {/* Payment */}
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 border-t pt-2">
                      <div><Label className="text-[11px]">{t('bookingCal.payStatus', 'Payment')}</Label>
                        <Select value={it.payStatus} onValueChange={v => setItem(it._key, { payStatus: v })}>
                          <SelectTrigger className="h-9" data-testid={`multidate-paystatus-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unpaid">{t('bookingCal.payUnpaid', 'Unpaid')}</SelectItem>
                            <SelectItem value="paid">{t('bookingCal.payPaid', 'Paid')}</SelectItem>
                            <SelectItem value="partial">{t('bookingCal.payPartial', 'Part paid')}</SelectItem>
                            <SelectItem value="free">{t('bookingCal.payFree', 'Free')}</SelectItem>
                          </SelectContent>
                        </Select></div>
                      {it.payStatus !== 'free' && <>
                        <div><Label className="text-[11px]">{t('bookingCal.payPrice', 'Price (€)')}</Label>
                          <Input type="number" min="0" step="0.01" value={it.payPrice} onChange={e => setItem(it._key, { payPrice: e.target.value })} data-testid={`multidate-price-${i}`} /></div>
                        {it.payStatus === 'partial' && <div><Label className="text-[11px]">{t('bookingCal.payAmount', 'Paid (€)')}</Label>
                          <Input type="number" min="0" step="0.01" value={it.payAmount} onChange={e => setItem(it._key, { payAmount: e.target.value })} data-testid={`multidate-amount-${i}`} /></div>}
                      </>}
                    </div>
                  </div>
                );
              })}
              {!items.length && <p className="text-center text-sm text-gray-500 py-6" data-testid="multidate-empty">{t('bookingCal.md_noneLeft', 'No appointments left — go back to pick dates.')}</p>}
            </div>
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={close} data-testid="multidate-cancel-edit">{t('cancel', 'Cancel')}</Button>
              <Button onClick={goReview} disabled={!items.length} data-testid="multidate-review">{t('bookingCal.md_reviewSave', 'Review & save')}</Button>
            </div>
          </div>
        )}

        {/* STEP 3 — review & save */}
        {step === 'review' && (
          <div data-testid="multidate-step-review">
            <p className="text-[13px] text-gray-600 mb-2">{t('bookingCal.md_reviewHint', 'These appointments will be created as independent bookings.')}</p>
            {hasConflicts && (
              <div className="mb-2 rounded-lg bg-amber-50 border border-amber-300 p-2 text-[12px] text-amber-800 flex items-start gap-2" data-testid="multidate-review-conflict">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                {t('bookingCal.md_reviewConflict', 'Some appointments still overlap. You can go back to change them or save anyway.')}
              </div>
            )}
            <div className="space-y-1.5 max-h-[50dvh] overflow-y-auto" data-testid="multidate-summary">
              {items.map((it, i) => (
                <div key={it._key} className={`rounded-lg border p-2 text-[13px] flex items-center justify-between ${conflicts[it._key] ? 'border-amber-400 bg-amber-50/40' : 'border-gray-200'}`} data-testid={`multidate-summary-${i}`}>
                  <div>
                    <span className="font-semibold">{format(new Date(it.date + 'T00:00:00'), 'EEE d MMM yyyy')}</span>
                    <span className="text-gray-600"> · {it.startTime}–{it.endTime} · {it.clientName || '—'}</span>
                    <span className="text-gray-400"> · {memberName(it.assigneeUserId)}</span>
                  </div>
                  {conflicts[it._key] && <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />}
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={() => setStep('edit')} data-testid="multidate-back-edit">
                <ChevronLeft className="h-4 w-4 mr-1" />{t('bookingCal.md_back', 'Back')}
              </Button>
              <Button onClick={saveAll} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="multidate-save-all">
                <CheckCircle2 className="h-4 w-4 mr-1" />
                {saving ? t('bookingCal.md_saving', 'Saving…') : t('bookingCal.md_saveAll', 'Save all appointments ({n})').replace('{n}', items.length)}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
